import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './PreExperiment.css';

interface PreExperimentProps {
  onComplete: (demographicData: DemographicData) => void;
}

interface DemographicData {
  // Coding-related stratification fields ONLY (for task routing)
  workDivision: string;
  codingPercentage: string;
  yearsOfCoding: string;
}

const PreExperiment: React.FC<PreExperimentProps> = ({ onComplete }) => {
  const navigate = useNavigate();
  
  React.useEffect(() => {
    console.log('PreExperiment component mounted');
    return () => console.log('PreExperiment component unmounted');
  }, []);
  
  const [formData, setFormData] = useState<DemographicData>({
    workDivision: '',
    codingPercentage: '',
    yearsOfCoding: ''
  });

  const [currentSection, setCurrentSection] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Development mode: Check URL parameter to skip validation
  const urlParams = new URLSearchParams(window.location.search);
  const skipValidation = urlParams.get('dev') === 'true';

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateSection = (): boolean => {
    // Skip validation in dev mode
    if (skipValidation) {
      return true;
    }
    
    const newErrors: Record<string, string> = {};
    const currentFields = sections[currentSection].fields;
    
    currentFields.forEach(field => {
      if (field.required && !formData[field.name as keyof DemographicData]) {
        newErrors[field.name] = `${field.label} is required`;
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateSection()) {
      if (currentSection < sections.length - 1) {
        setCurrentSection(currentSection + 1);
      } else {
        handleSubmit();
      }
    }
  };

  const handleBack = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
    }
  };

  const handleSubmit = async () => {
    // In dev mode, provide default data for any missing fields
    let dataToSubmit = formData;
    if (skipValidation) {
      dataToSubmit = {
        workDivision: 'coder',
        codingPercentage: '75',
        yearsOfCoding: '5'
      } as DemographicData;
    }
    // Pass all data to parent, which will save after initialization
    onComplete(dataToSubmit);
  };


  const sections: Array<{
    title: string;
    fields: Array<{
      name: string;
      label: string;
      type: string;
      required: boolean;
      options?: Array<{ value: string; label: string }>;
      showIf?: string;
      showValue?: string;
    }>;
  }> = [
    {
      title: 'Work Background',
      fields: [
        {
          name: 'workDivision',
          label: 'Work Division',
          type: 'select',
          required: true,
          options: [
            { value: '', label: 'Select your work division' },
            { value: 'coder', label: 'Coder - My work involves programming/coding' },
            { value: 'non-coder', label: 'Non-Coder - My work does not involve programming/coding' }
          ]
        },
        {
          name: 'codingPercentage',
          label: 'What percentage of your weekly work involves coding/programming?',
          type: 'select',
          required: true,
          options: [
            { value: '', label: 'Select percentage' },
            { value: '0', label: '0% - No coding work' },
            { value: '10', label: '1-10%' },
            { value: '25', label: '11-25%' },
            { value: '50', label: '26-50%' },
            { value: '75', label: '51-75%' },
            { value: '90', label: '76-90%' },
            { value: '100', label: '91-100%' }
          ]
        },
        {
          name: 'yearsOfCoding',
          label: 'Years of coding/programming experience',
          type: 'select',
          required: true,
          options: [
            { value: '', label: 'Select experience' },
            { value: '0', label: 'No experience' },
            { value: '1', label: 'Less than 1 year' },
            { value: '2', label: '1-2 years' },
            { value: '5', label: '3-5 years' },
            { value: '10', label: '6-10 years' },
            { value: '15', label: '11-15 years' },
            { value: '20', label: '16-20 years' },
            { value: '25', label: 'More than 20 years' }
          ]
        }
      ]
    }
  ];

  const currentSectionData = sections[currentSection];
  const progress = ((currentSection + 1) / sections.length) * 100;

  return (
    <div className="pre-experiment">
      <div className="pre-experiment-container">
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${progress}%` }} />
        </div>
        
        <h2>Brief Background Survey</h2>
        <h3>{currentSectionData.title}</h3>
        
        {skipValidation && (
          <div style={{ backgroundColor: '#ffe4b5', padding: '10px', marginBottom: '20px', borderRadius: '4px' }}>
            <strong>Dev Mode:</strong> Validation disabled. 
            <button 
              type="button" 
              className="btn btn-warning" 
              style={{ marginLeft: '10px' }}
              onClick={() => handleSubmit()}
            >
              Skip to Email Task
            </button>
          </div>
        )}
        
        <form className="survey-form">
          {currentSectionData.fields.map((field) => (
            <div key={field.name} className="form-group">
              <label htmlFor={field.name}>
                {field.label}
                {field.required && <span className="required">*</span>}
              </label>
              
              <select
                id={field.name}
                name={field.name}
                value={formData[field.name as keyof DemographicData]}
                onChange={handleChange}
                className={errors[field.name] ? 'error' : ''}
              >
                {field.options?.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              
              {errors[field.name] && (
                <span className="error-message">{errors[field.name]}</span>
              )}
            </div>
          ))}
          
          <div className="form-actions">
            {currentSection > 0 && (
              <button type="button" className="btn btn-secondary" onClick={handleBack}>
                Back
              </button>
            )}
            <button type="button" className="btn btn-primary" onClick={handleNext}>
              {currentSection < sections.length - 1 ? 'Next' : 'Begin Experiment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PreExperiment;